import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import clinicService from '../../../../../services/clinic.service';
import { toast } from 'react-toastify';

const ClinicOverviewForm = ({ clinicinfo, onSaved, onCancel }) => {
    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: clinicinfo,
    });
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        reset(clinicinfo);
    }, [clinicinfo, reset]);

    const cleanPhoneNumber = (phoneNumber) => {
        return phoneNumber.replace(/\D/g, '');
    };

    const onSubmit = (data) => {
        setIsLoading(true);
        const newData = {
            id: data.id,
            name: data.name,
            email: data.email,
            phone: cleanPhoneNumber(data.phone),
            sms: cleanPhoneNumber(data.sms),
            logo: data.logo,
            signature: data.signature,
            website: data.website,
            clinicType: data.clinicType,
            street: data.street,
            city: data.city,
            postalCode: data.postalCode,
            province: data.province,
            country: data.country,
        };
        clinicService
            .updateClinic(clinicinfo.id, newData)
            .then(() => {
                toast.success('Clinic updated successfully', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
                onSaved?.();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Failed to update clinic', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="clinic-info-section-title clinic-info-section-title--first">
                Basic Information
            </div>
            <div className="row g-3">
                <div className="col-md-6">
                    <label htmlFor="clinicType" className="notifications-form-label">
                        Type
                    </label>
                    <select
                        className="form-select form-select-sm"
                        id="clinicType"
                        disabled={isLoading}
                        {...register('clinicType', { required: true })}
                    >
                        <option value="EYECONX">EYECONX</option>
                        <option value="SMS_GATEWAY">SMS GATEWAY</option>
                        <option value="ADMIN">ADMIN</option>
                    </select>
                </div>
                <div className="col-md-6">
                    <label htmlFor="name" className="notifications-form-label">
                        Name
                    </label>
                    <input
                        type="text"
                        id="name"
                        className={`form-control form-control-sm ${errors.name ? 'is-invalid' : ''}`}
                        placeholder="Clinic name"
                        disabled={isLoading}
                        {...register('name', { required: true })}
                    />
                    {errors.name && <div className="invalid-feedback">Name is required</div>}
                </div>
            </div>

            <div className="clinic-info-section-title">Contact Information</div>
            <div className="row g-3">
                <div className="col-md-6">
                    <label htmlFor="email" className="notifications-form-label">
                        Email
                    </label>
                    <input
                        type="email"
                        id="email"
                        className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                        placeholder="Clinic email"
                        disabled={isLoading}
                        {...register('email', { required: true })}
                    />
                    {errors.email && <div className="invalid-feedback">Email is required</div>}
                </div>
                <div className="col-md-6">
                    <label htmlFor="phone" className="notifications-form-label">
                        Phone
                    </label>
                    <input
                        type="text"
                        id="phone"
                        className={`form-control form-control-sm ${errors.phone ? 'is-invalid' : ''}`}
                        placeholder="Clinic phone"
                        disabled={isLoading}
                        {...register('phone', { required: true })}
                    />
                    {errors.phone && <div className="invalid-feedback">Phone is required</div>}
                </div>
                <div className="col-md-6">
                    <label htmlFor="sms" className="notifications-form-label">
                        SMS
                    </label>
                    <input
                        type="text"
                        id="sms"
                        className={`form-control form-control-sm ${errors.sms ? 'is-invalid' : ''}`}
                        placeholder="Clinic SMS number"
                        disabled={isLoading}
                        {...register('sms', { required: true })}
                    />
                    {errors.sms && <div className="invalid-feedback">SMS is required</div>}
                </div>
                <div className="col-md-6">
                    <label htmlFor="website" className="notifications-form-label">
                        Website
                    </label>
                    <input
                        type="text"
                        id="website"
                        className={`form-control form-control-sm ${errors.website ? 'is-invalid' : ''}`}
                        placeholder="https://example.com"
                        disabled={isLoading}
                        {...register('website', { required: true })}
                    />
                    {errors.website && <div className="invalid-feedback">Website is required</div>}
                </div>
            </div>

            <div className="clinic-info-section-title">Branding</div>
            <div className="row g-3">
                <div className="col-md-6">
                    <label htmlFor="signature" className="notifications-form-label">
                        Signature
                    </label>
                    <input
                        type="text"
                        id="signature"
                        className={`form-control form-control-sm ${errors.signature ? 'is-invalid' : ''}`}
                        placeholder="Clinic signature"
                        disabled={isLoading}
                        {...register('signature', { required: true })}
                    />
                    {errors.signature && <div className="invalid-feedback">Signature is required</div>}
                </div>
                <div className="col-md-6">
                    <label htmlFor="logo" className="notifications-form-label">
                        Logo URL
                    </label>
                    <input
                        type="text"
                        id="logo"
                        className="form-control form-control-sm"
                        placeholder="https://..."
                        disabled={isLoading}
                        {...register('logo')}
                    />
                </div>
            </div>

            <div className="clinic-module__form-actions">
                {onCancel && (
                    <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={onCancel}
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                )}
                <button type="submit" className="btn btn-success btn-sm" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Saving...
                        </>
                    ) : (
                        <>
                            <i className="bi bi-floppy me-1"></i>
                            Save Changes
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default ClinicOverviewForm;
